import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTruck } from '@fortawesome/free-solid-svg-icons';
import { appendTimeZone } from '@s20/common/util/date';
import { INTERPUNCT } from '../../../common/util';
import { useNow } from './useNow';

const ARRIVING_THRESHOLD_SECONDS = 60;
// If the arrival is this far past, the live feed has likely stalled (no fresh push) — hide the
// chip rather than count down forever into a stuck "Arriving now".
const STALE_AFTER_SECONDS = 300;

/**
 * Uber-style arrival chip: crew lead, minutes away (counting down live), and arrival time in the
 * job's timezone. Renders nothing until an ETA is available. The "min away" is derived from the
 * arrival time vs. a ticking `now`, so it counts down between server pushes; once the crew is
 * within the threshold (or the arrival has passed) it shows "Arriving now".
 */
export const EtaDisplay = ({
    estimatedArrivalTime, crewLeadName, startTime, timeZone,
}) => {
    const now = useNow(30_000);

    if (!estimatedArrivalTime) {
        return null;
    }

    const secondsAway = moment(estimatedArrivalTime).diff(now, 'seconds');

    if (secondsAway < -STALE_AFTER_SECONDS) {
        return null;
    }

    const isArriving = secondsAway < ARRIVING_THRESHOLD_SECONDS;
    const minutesAway = Math.max(1, Math.round(secondsAway / 60));

    let arrivalText = null;
    if (!isArriving) {
        // Show arrival in the job site's timezone to match the panel's other times ("Starts At").
        const arrival = startTime
            ? moment(estimatedArrivalTime).utcOffset(moment.parseZone(startTime).utcOffset())
            : moment(estimatedArrivalTime);
        arrivalText = appendTimeZone(arrival.format('h:mm A'), startTime, timeZone);
    }

    return (
        <div className="d-flex align-items-center bg-light rounded p-2 mb-2">
            <FontAwesomeIcon
                icon={faTruck}
                size="lg"
                className="text-primary mr-2"
            />

            <div>
                <div className="font-weight-bold">
                    {crewLeadName ? `${crewLeadName} ${INTERPUNCT} ` : ''}
                    {isArriving ? 'Arriving now' : `~${minutesAway} min away`}
                </div>

                {Boolean(arrivalText) && (
                    <div className="text-muted small">
                        {`Arrives ${arrivalText}`}
                    </div>
                )}
            </div>
        </div>
    );
};
